import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  Building2,
  ChevronDown,
  Download,
  Forward,
  Inbox,
  Mail,
  Reply,
  ReplyAll,
  Star,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  formatDateTimeWithRelative,
  type MailerComposeMode,
  type MailerMessage,
  type MailerStatus,
  type MailerThreadAction,
  type MailerThreadDetail,
} from '@nesc/shared';
import { api, downloadFile, errorMessage, type SingleResponse } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { SkeletonRows } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/common/feedback';
import { PageHeader } from '@/components/common/page';
import { Composer, type ComposerInitial } from '@/features/mail/composer';
import { MailHtmlBody, MailTextBody } from '@/features/mail/mail-body';
import {
  addressLabel,
  formatBytes,
  listDate,
  prefixSubject,
  replyRecipients,
  withSignature,
} from '@/features/mail/compose-utils';
import { MailerNotConnected, useMailerStatus } from './mail';
import { cn } from '@/lib/utils';

/** S53 メールのスレッド。Gmail と同じく古いメールは折りたたみ、最後と未読を開いて表示する */
export const MailThreadPage = () => {
  const { threadId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const folder = searchParams.get('folder') ?? 'INBOX';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [composer, setComposer] = useState<ComposerInitial | null>(null);

  const status = useMailerStatus();
  const connected = status.data?.data.connected ?? false;

  const thread = useQuery({
    queryKey: qk.mailerThread(threadId),
    queryFn: () => api.get<SingleResponse<MailerThreadDetail>>(`/mailer/threads/${threadId}`),
    enabled: connected && !!threadId,
  });

  // 開いたスレッドはサーバー側で既読にしているため、一覧と未読数を取り直す
  useEffect(() => {
    if (!thread.dataUpdatedAt) return;
    void queryClient.invalidateQueries({ queryKey: ['mailer', 'threads'] });
    void queryClient.invalidateQueries({ queryKey: qk.mailerStatus });
  }, [thread.dataUpdatedAt, queryClient]);

  const backToList = () => navigate(`/mail?folder=${folder}`);

  const act = useMutation({
    mutationFn: (action: MailerThreadAction) => api.post('/mailer/threads/actions', { threadIds: [threadId], action }),
    onSuccess: (_result, action) => {
      void queryClient.invalidateQueries({ queryKey: ['mailer'] });
      if (action === 'archive' || action === 'trash' || action === 'unread' || action === 'spam') {
        toast.success(
          action === 'archive'
            ? 'アーカイブしました'
            : action === 'trash'
              ? 'ゴミ箱に移動しました'
              : action === 'spam'
                ? '迷惑メールにしました'
                : '未読にしました',
        );
        backToList();
      }
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  if (status.isLoading) return <SkeletonRows rows={6} />;
  if (!connected) return <MailerNotConnected />;
  if (thread.isLoading) return <SkeletonRows rows={6} />;
  if (thread.isError || !thread.data) {
    return (
      <>
        <PageHeader back={backToList} title="メール" />
        <EmptyState title="メールを開けませんでした" description={errorMessage(thread.error)} />
      </>
    );
  }

  const me = status.data!.data;
  const detail = thread.data.data;
  const messages = detail.messages;
  const last = messages[messages.length - 1];
  const starred = detail.labelIds.includes('STARRED');
  const inTrash = detail.labelIds.includes('TRASH');
  const inInbox = detail.labelIds.includes('INBOX');

  const isOpen = (message: MailerMessage) =>
    expanded.has(message.id) || message.id === last?.id || message.unread || messages.length <= 2;

  const startCompose = (message: MailerMessage, mode: MailerComposeMode) => {
    const recipients = replyRecipients(message, mode, me.email ?? '');
    setComposer({
      mode,
      ...recipients,
      subject: prefixSubject(message.subject || detail.subject, mode),
      body: withSignature('', me.signature),
      sourceMessageId: message.id,
      sourceAttachmentCount: message.attachments.length,
    });
    // 作成欄までスクロールする
    setTimeout(() => document.getElementById('mail-composer')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
  };

  return (
    <>
      <PageHeader
        back={backToList}
        title={detail.subject || '（件名なし）'}
        actions={
          <>
            {inTrash ? (
              <Button variant="outline" onClick={() => act.mutate('untrash')}>
                <Inbox aria-hidden />
                ゴミ箱から戻す
              </Button>
            ) : (
              <>
                {inInbox ? (
                  <Button variant="outline" size="icon" onClick={() => act.mutate('archive')} aria-label="アーカイブ" title="アーカイブ">
                    <Archive aria-hidden />
                  </Button>
                ) : (
                  <Button variant="outline" size="icon" onClick={() => act.mutate('inbox')} aria-label="受信トレイに移動" title="受信トレイに移動">
                    <Inbox aria-hidden />
                  </Button>
                )}
                <Button variant="outline" size="icon" onClick={() => act.mutate('trash')} aria-label="削除" title="削除">
                  <Trash2 aria-hidden />
                </Button>
              </>
            )}
            <Button variant="outline" size="icon" onClick={() => act.mutate('unread')} aria-label="未読にする" title="未読にする">
              <Mail aria-hidden />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => act.mutate(starred ? 'unstar' : 'star')}
              aria-label={starred ? 'スターを外す' : 'スターを付ける'}
              aria-pressed={starred}
              title={starred ? 'スターを外す' : 'スターを付ける'}
            >
              <Star aria-hidden className={cn(starred && 'fill-foreground')} />
            </Button>
          </>
        }
      />

      {(detail.company || detail.inboundMessageId) && (
        <Card className="mb-3">
          <CardContent className="flex flex-wrap items-center gap-2 pt-4">
            {detail.company && (
              <Button variant="link" onClick={() => navigate(`/companies/${detail.company!.id}`)}>
                <Building2 aria-hidden />
                {detail.company.name}
              </Button>
            )}
            {detail.inboundMessageId && (
              <Button variant="link" onClick={() => navigate(`/inbox/${detail.inboundMessageId}`)}>
                受信（分類・紐付け）を開く
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      <div className="space-y-2">
        {messages.map((message) =>
          isOpen(message) ? (
            <MessageCard
              key={message.id}
              message={message}
              me={me}
              collapsible={message.id !== last?.id}
              onCollapse={() =>
                setExpanded((prev) => {
                  const next = new Set(prev);
                  next.delete(message.id);
                  return next;
                })
              }
              onCompose={(mode) => startCompose(message, mode)}
            />
          ) : (
            <button
              key={message.id}
              type="button"
              onClick={() => setExpanded((prev) => new Set(prev).add(message.id))}
              className="flex w-full items-center gap-3 rounded-card border border-border bg-surface px-4 py-2 text-left hover:bg-muted"
            >
              <span className="w-40 shrink-0 truncate text-body font-semibold">{addressLabel(message.from, me.email ?? '')}</span>
              <span className="min-w-0 flex-1 truncate text-body text-foreground-muted">{message.snippet}</span>
              <span className="tabular shrink-0 text-note text-foreground-muted">{listDate(message.date)}</span>
            </button>
          ),
        )}
      </div>

      {composer ? (
        <div id="mail-composer" className="mt-4">
          <Composer
            key={`${composer.mode}-${composer.sourceMessageId}`}
            initial={composer}
            signature={me.signature}
            safeModeAllowlist={me.safeModeAllowlist}
            onSent={(result) => {
              setComposer(null);
              void queryClient.invalidateQueries({ queryKey: qk.mailerThread(threadId) });
              if (result.threadId && result.threadId !== threadId) navigate(`/mail/${result.threadId}?folder=SENT`);
            }}
            onDiscard={() => setComposer(null)}
          />
        </div>
      ) : (
        last && (
          <div className="mt-4 flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => startCompose(last, 'reply')}>
              <Reply aria-hidden />
              返信
            </Button>
            {last.to.length + last.cc.length > 1 && (
              <Button variant="outline" onClick={() => startCompose(last, 'replyAll')}>
                <ReplyAll aria-hidden />
                全員に返信
              </Button>
            )}
            <Button variant="outline" onClick={() => startCompose(last, 'forward')}>
              <Forward aria-hidden />
              転送
            </Button>
          </div>
        )
      )}
    </>
  );
};

const MessageCard = ({
  message,
  me,
  collapsible,
  onCollapse,
  onCompose,
}: {
  message: MailerMessage;
  me: MailerStatus;
  collapsible: boolean;
  onCollapse: () => void;
  onCompose: (mode: MailerComposeMode) => void;
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [showImages, setShowImages] = useState(false);
  const hasRemoteImages = !!message.html && /<img[^>]+src=["']?https?:/i.test(message.html);
  const recipientsLabel = [...message.to, ...message.cc].map((a) => addressLabel(a, me.email ?? '')).join(', ');

  return (
    <Card>
      <CardContent className="space-y-3 pt-4">
        <div className="flex items-start gap-2">
          <button
            type="button"
            className={cn('min-w-0 flex-1 text-left', !collapsible && 'cursor-default')}
            onClick={() => collapsible && onCollapse()}
            aria-label={collapsible ? '折りたたむ' : undefined}
          >
            <p className="text-body">
              <span className="font-semibold">{message.from?.name || message.from?.email}</span>
              {message.from?.name && <span className="text-note text-foreground-muted"> &lt;{message.from.email}&gt;</span>}
            </p>
          </button>
          <span className="tabular shrink-0 text-note text-foreground-muted">{formatDateTimeWithRelative(message.date)}</span>
          <Button variant="ghost" size="icon-sm" onClick={() => onCompose('reply')} aria-label="返信" title="返信">
            <Reply aria-hidden />
          </Button>
        </div>

        <button
          type="button"
          onClick={() => setShowDetails((prev) => !prev)}
          className="flex items-center gap-1 text-note text-foreground-muted hover:underline"
          aria-expanded={showDetails}
        >
          宛先: {recipientsLabel || '（なし）'}
          <ChevronDown aria-hidden className={cn('size-3.5 transition-transform', showDetails && 'rotate-180')} />
        </button>
        {showDetails && (
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 rounded-control bg-surface p-2 text-note">
            <dt className="text-foreground-muted">差出人</dt>
            <dd className="break-all">{message.from ? `${message.from.name ?? ''} <${message.from.email}>` : ''}</dd>
            <dt className="text-foreground-muted">宛先</dt>
            <dd className="break-all">{message.to.map((a) => a.email).join(', ')}</dd>
            {message.cc.length > 0 && (
              <>
                <dt className="text-foreground-muted">Cc</dt>
                <dd className="break-all">{message.cc.map((a) => a.email).join(', ')}</dd>
              </>
            )}
            <dt className="text-foreground-muted">件名</dt>
            <dd className="break-all">{message.subject}</dd>
          </dl>
        )}

        {message.html ? (
          <>
            {hasRemoteImages && !showImages && (
              <p className="flex flex-wrap items-center gap-2 rounded-control border border-border px-3 py-2 text-note text-foreground-muted">
                外部の画像は表示していません（開封の通知に使われることがあります）
                <Button variant="link" size="sm" onClick={() => setShowImages(true)}>
                  画像を表示
                </Button>
              </p>
            )}
            <MailHtmlBody html={message.html} allowRemoteImages={showImages} />
          </>
        ) : (
          <MailTextBody text={message.text ?? ''} />
        )}

        {message.attachments.length > 0 && (
          <ul className="flex flex-wrap gap-2 border-t border-border pt-3">
            {message.attachments.map((attachment) => (
              <li key={attachment.index}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    void downloadFile(
                      `/mailer/messages/${message.id}/attachments/${attachment.index}`,
                      attachment.filename,
                    ).catch((error) => toast.error(errorMessage(error)))
                  }
                  title={`${attachment.filename}（${formatBytes(attachment.size)}）をダウンロード`}
                >
                  <Download aria-hidden />
                  <span className="max-w-48 truncate">{attachment.filename}</span>
                  <span className="tabular text-foreground-muted">{formatBytes(attachment.size)}</span>
                </Button>
              </li>
            ))}
          </ul>
        )}

        <div className="flex flex-wrap gap-2 border-t border-border pt-3">
          <Button variant="ghost" size="sm" onClick={() => onCompose('reply')}>
            <Reply aria-hidden />
            返信
          </Button>
          {message.to.length + message.cc.length > 1 && (
            <Button variant="ghost" size="sm" onClick={() => onCompose('replyAll')}>
              <ReplyAll aria-hidden />
              全員に返信
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => onCompose('forward')}>
            <Forward aria-hidden />
            転送
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
